import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Columns, Filter, ArrowDownRight, ArrowUpRight, ChevronDown, Calendar } from 'lucide-react';
import { motion } from 'framer-motion';
const chartData = [{
  month: 'May-25',
  low: 10,
  medium: 10,
  high: 10
}, {
  month: 'Jun-25',
  low: 12,
  medium: 12,
  high: 8
}, {
  month: 'Jul-25',
  low: 15,
  medium: 15,
  high: 10
}, {
  month: 'Aug-25',
  low: 18,
  medium: 18,
  high: 14
}, {
  month: 'Sep-25',
  low: 20,
  medium: 22,
  high: 20
}, {
  month: 'Oct-25',
  low: 22,
  medium: 25,
  high: 23
}, {
  month: 'Nov-25',
  low: 25,
  medium: 30,
  high: 27
}, {
  month: 'Dec-25',
  low: 28,
  medium: 35,
  high: 32
}, {
  month: 'Jan-26',
  low: 30,
  medium: 38,
  high: 32
}, {
  month: 'Feb-26',
  low: 30,
  medium: 40,
  high: 30
}, {
  month: 'Mar-26',
  low: 30,
  medium: 40,
  high: 30
}, {
  month: 'Apr-26',
  low: 30,
  medium: 40,
  high: 30
}];
const tableData = [{
  id: '2',
  company: 'DUNDER MIFFLIN PAPER CO.',
  score: 82,
  trend: 3,
  dbt: 0,
  balance: 0,
  limit: 'Pending Decision',
  assignedTo: 'Sarah Chen',
  group: 'L'
}, {
  id: '1',
  company: 'WONKA INDUSTRIES',
  score: 88,
  trend: 7,
  dbt: 5,
  balance: 35000,
  limit: 100000,
  assignedTo: 'Dave Davidson',
  group: 'L'
}, {
  id: '5',
  company: 'INITECH',
  score: 28,
  trend: -12,
  dbt: 45,
  balance: 68000,
  limit: 75000,
  assignedTo: 'Sarah Chen',
  group: 'H'
}, {
  id: '3',
  company: 'WAYNE ENTERPRISES',
  score: 56,
  trend: -1,
  dbt: 29,
  balance: 38000,
  limit: 60000,
  assignedTo: 'Dave Davidson',
  group: 'M'
}, {
  id: '6',
  company: 'BIG KAHUNA BURGER',
  score: 28,
  trend: -8,
  dbt: 52,
  balance: 35000,
  limit: 37000,
  assignedTo: 'Sarah Chen',
  group: 'H'
}, {
  id: '7',
  company: 'BUY N LARGE',
  score: 56,
  trend: 2,
  dbt: 18,
  balance: 42000,
  limit: 80000,
  assignedTo: 'Dave Davidson',
  group: 'M'
}];
function formatCurrency(value: number) {
  return `$${(value / 1000).toFixed(0)}K`;
}
function getScoreColor(score: number) {
  if (score <= 30) return 'text-red-500 border-red-200 bg-red-50';
  if (score <= 70) return 'text-amber-500 border-amber-200 bg-amber-50';
  return 'text-green-500 border-green-200 bg-green-50';
}
function getRiskLevel(score: number): 'high' | 'moderate' | 'low' {
  if (score <= 30) return 'high';
  if (score <= 70) return 'moderate';
  return 'low';
}
function getTrendColor(trend: number) {
  if (trend < 0) return 'text-red-500';
  return 'text-green-500';
}
const CustomLegend = (props: any) => {
  const {
    payload
  } = props;
  return <div className="flex items-center justify-end gap-4 text-xs font-medium text-gray-500" data-id="element-2677" data-mp="3gbdkb6wu1cjm">
      {payload.map((entry: any, index: number) => <div key={`item-${index}`} className="flex items-center gap-1.5" data-id="element-2678" data-mp="3bbher38hld1y" data-mpi={index}>
          <div className="w-3 h-3 rounded-sm" style={{
        backgroundColor: entry.color
      }} data-id="element-2679" data-mp="3pck2rlj1kxfi" />
          <span className="capitalize" data-id="element-2680" data-mp="3lb8dxi7zsg8d">{entry.value}</span>
        </div>)}
    </div>;
};
interface MonitorPageProps {
  onCompanyClick: (company: string) => void;
}
export function MonitorPage({
  onCompanyClick
}: MonitorPageProps) {
  const [selectedRisk, setSelectedRisk] = useState<'all' | 'high' | 'moderate' | 'low'>('all');
  const [showGroupDropdown, setShowGroupDropdown] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const filteredData = selectedRisk === 'all' ? tableData : tableData.filter(row => getRiskLevel(row.score) === selectedRisk);
  return <div className="p-6 max-w-[1600px] mx-auto w-full space-y-6" data-id="element-2681" data-mp="2mksoohqhncyj">
      {/* Page Title */}
      <h1 className="text-3xl font-bold text-gray-900" data-id="element-2682" data-mp="2rjlt7knsrtmk">
        Acme's Credit Dashboard
      </h1>

      {/* Top Section */}
      <div className="flex flex-col lg:flex-row gap-6" data-id="element-2683" data-mp="2wif6bbz69im7">
        {/* Stat Cards Grid */}
        <div className="grid grid-cols-2 gap-4 lg:w-[400px] shrink-0" data-id="element-2684" data-mp="319x985kr9t9t">
          <button onClick={() => setSelectedRisk('all')} className={`bg-white rounded-xl border p-5 flex flex-col justify-center shadow-sm transition-all ${selectedRisk === 'all' ? 'border-2 border-gray-500 bg-gray-50' : selectedRisk !== 'all' ? 'border-gray-200 opacity-40' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-2685" data-mp="31b8ds7m0z51z">
            <div className={`text-4xl font-bold mb-1 ${selectedRisk === 'all' ? 'text-gray-900' : 'text-gray-400'}`} data-id="element-2686" data-mp="3k6ygjx1s85gx">
              88
            </div>
            <div className={`text-xs font-bold tracking-wider uppercase ${selectedRisk === 'all' ? 'text-gray-600' : 'text-gray-400'}`} data-id="element-2687" data-mp="1ptgpw162ysho">
              Total
              <br data-id="element-2688" data-mp="1djh9yrdewkwc" />
              Accounts
            </div>
          </button>
          <button onClick={() => setSelectedRisk('high')} className={`bg-white rounded-xl border-2 p-5 flex flex-col justify-center shadow-sm transition-all ${selectedRisk === 'high' ? 'border-red-500 bg-red-50' : selectedRisk !== 'all' ? 'border-red-300 opacity-40' : 'border-red-400 hover:border-red-500'}`} data-id="element-2689" data-mp="1di69b0f9jt9g">
            <div className={`text-4xl font-bold mb-1 ${selectedRisk === 'high' ? 'text-red-600' : 'text-red-400'}`} data-id="element-2690" data-mp="1wbjw2i8totdz">
              19
            </div>
            <div className={`text-xs font-bold tracking-wider uppercase ${selectedRisk === 'high' ? 'text-red-600' : 'text-red-400'}`} data-id="element-2691" data-mp="1mcuc8jxd8yv3">
              High Risk
            </div>
          </button>
          <button onClick={() => setSelectedRisk('moderate')} className={`bg-white rounded-xl border-2 p-5 flex flex-col justify-center shadow-sm transition-all ${selectedRisk === 'moderate' ? 'border-amber-500 bg-amber-50' : selectedRisk !== 'all' ? 'border-amber-300 opacity-40' : 'border-amber-400 hover:border-amber-500'}`} data-id="element-2692" data-mp="awe318cenego">
            <div className={`text-4xl font-bold mb-1 ${selectedRisk === 'moderate' ? 'text-amber-600' : 'text-amber-400'}`} data-id="element-2693" data-mp="2u3rma9oa8dgf">
              47
            </div>
            <div className={`text-xs font-bold tracking-wider uppercase ${selectedRisk === 'moderate' ? 'text-amber-600' : 'text-amber-400'}`} data-id="element-2694" data-mp="2k5tz7pi5smpe">
              Moderate Risk
            </div>
          </button>
          <button onClick={() => setSelectedRisk('low')} className={`bg-white rounded-xl border-2 p-5 flex flex-col justify-center shadow-sm transition-all ${selectedRisk === 'low' ? 'border-green-500 bg-green-50' : selectedRisk !== 'all' ? 'border-green-300 opacity-40' : 'border-green-400 hover:border-green-500'}`} data-id="element-2695" data-mp="2belal6lwom44">
            <div className={`text-4xl font-bold mb-1 ${selectedRisk === 'low' ? 'text-green-600' : 'text-green-400'}`} data-id="element-2696" data-mp="1hvdn0iygfuh7">
              22
            </div>
            <div className={`text-xs font-bold tracking-wider uppercase ${selectedRisk === 'low' ? 'text-green-600' : 'text-green-400'}`} data-id="element-2697" data-mp="1rpngy531p0u1">
              Low Risk
            </div>
          </button>
        </div>

        {/* Chart */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 flex-1 shadow-sm min-h-[240px] flex flex-col" data-id="element-2698" data-mp="1wps062vc89ny">
          <div className="flex items-center justify-start gap-3 mb-6" data-id="element-2699" data-mp="2ahq2nuujn7b5">
            {/* Groups Dropdown */}
            <div className="relative" data-id="element-2700" data-mp="25jwwv2x5kfbe">
              <button onClick={() => setShowGroupDropdown(!showGroupDropdown)} className="flex items-center justify-between gap-2 px-4 py-2 border-2 border-indigo-500 rounded-lg text-sm font-medium text-indigo-600 hover:bg-indigo-50 transition-colors min-w-[160px]" data-id="element-2701" data-mp="2kg5tdwpnsek4">
                <span className="text-indigo-500 text-xs font-semibold" data-id="element-2702" data-mp="153jvgihfxck5">
                  Groups
                </span>
                <ChevronDown className="w-4 h-4 text-indigo-500" data-id="element-2703" data-mp="1a4hf6zi3menr" />
              </button>
              {showGroupDropdown && <div className="absolute top-full mt-2 left-0 bg-white border border-gray-200 rounded-lg shadow-lg py-2 w-56 z-10" data-id="element-2704" data-mp="1f06i9ctbudot">
                  {['Ungrouped', 'Agriculture', 'Biotech', 'Building Materials', 'Construction', 'Consumer Goods', 'Energy', 'Financial Services', 'Healthcare', 'Manufacturing', 'Retail', 'Technology', 'Transportation'].map(group => <label key={group} className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 cursor-pointer" data-id="element-2705" data-mp="2gxndbkb4aqe2">
                      <input type="checkbox" checked={selectedGroups.includes(group)} onChange={e => {
                  if (e.target.checked) {
                    setSelectedGroups([...selectedGroups, group]);
                  } else {
                    setSelectedGroups(selectedGroups.filter(g => g !== group));
                  }
                }} className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500" data-id="element-2706" data-mp="27wfhd8w2q1n5" />
                      <span className="text-sm text-gray-700" data-id="element-2707" data-mp="1f9uno1s4ldgf">{group}</span>
                    </label>)}
                </div>}
            </div>

            {/* Date Range Selector */}
            <div className="relative" data-id="element-2708" data-mp="2y1zz0kpw3l2f">
              <input type="text" placeholder="MM/DD/YYYY – MM/DD/YYYY" className="pl-4 pr-10 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent w-64" data-id="element-2709" data-mp="2y16xby5sflr5" />
              <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-2710" data-mp="2k308f7itgw3m" />
            </div>
          </div>

          <div className="flex-1 min-h-[180px]" data-id="element-2711" data-mp="2t2n9ttbjq1wo">
            <ResponsiveContainer width="100%" height="100%" data-id="element-2712" data-mp="2t4gi1d1t4u6s">
              <BarChart data={chartData} margin={{
              top: 0,
              right: 0,
              left: -20,
              bottom: 0
            }} barSize={24} data-id="element-2713" data-mp="2o3aml7gristk">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-2714" data-mp="3fnwzc9e0w4j" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#6B7280'
              }} dy={10} data-id="element-2715" data-mp="fx8ftf4a13ba" />
                <YAxis axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#6B7280'
              }} domain={[0, 100]} ticks={[0, 20, 40, 60, 80, 100]} data-id="element-2716" data-mp="uktkvekppclu" />
                <Tooltip cursor={{
                fill: '#F3F4F6'
              }} contentStyle={{
                borderRadius: '8px',
                border: '1px solid #E5E7EB',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
              }} data-id="element-2717" data-mp="pt22hrbpmd6s" />
                <Legend content={<CustomLegend data-id="element-2719" data-mp="1n71lvqd9s2vk" />} verticalAlign="top" align="right" wrapperStyle={{
                top: -40
              }} data-id="element-2718" data-mp="1s4aaa8p0h359" />
                <Bar dataKey="high" stackId="a" fill="#f87171" name="High" data-id="element-2720" data-mp="1e7myx0wbaaft" />
                <Bar dataKey="medium" stackId="a" fill="#fbbf24" name="Medium" data-id="element-2721" data-mp="197iitbzbfhq4" />
                <Bar dataKey="low" stackId="a" fill="#4ade80" name="Low" data-id="element-2722" data-mp="14ez5mg4jiolu" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col" data-id="element-2723" data-mp="ugt5a2ydtqj3">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-white" data-id="element-2724" data-mp="qj3puj5fttmo">
          <h3 className="text-lg font-bold text-gray-900" data-id="element-2725" data-mp="2x5jkzj8vqpp0">
            Monitored Accounts
          </h3>
          <div className="flex items-center gap-4" data-id="element-2726" data-mp="324mec6fhz7h5">
            <button className="flex items-center gap-2 text-sm font-semibold text-indigo-600 hover:text-indigo-700" data-id="element-2727" data-mp="3232y2ftucwfd">
              <Columns className="w-4 h-4" data-id="element-2728" data-mp="36710p0idkpd6" />
              COLUMNS
            </button>
            <button className="flex items-center gap-2 text-sm font-semibold text-indigo-600 hover:text-indigo-700" data-id="element-2729" data-mp="3b4sqnozgm1eq">
              <Filter className="w-4 h-4" data-id="element-2730" data-mp="2ecyx6g81fas2" />
              FILTERS
            </button>
          </div>
        </div>

        <div className="overflow-x-auto" data-id="element-2731" data-mp="2nfq90h4ts8k8">
          <table className="w-full text-left text-sm" data-id="element-2732" data-mp="2neovm60c79zc">
            <thead className="bg-white border-b border-gray-100" data-id="element-2733" data-mp="2s6gd7r1im5dc">
              <tr data-id="element-2734" data-mp="2s7ri5u6p5j61">
                <th className="px-6 py-4 font-semibold text-gray-500 text-xs tracking-wider uppercase whitespace-nowrap" data-id="element-2735" data-mp="2s8b1nfbzwfm6">
                  Company
                </th>
                <th className="px-6 py-4 font-semibold text-gray-500 text-xs tracking-wider uppercase whitespace-nowrap" data-id="element-2736" data-mp="1zokty46plkaz">
                  Pulse Score
                </th>
                <th className="px-6 py-4 font-semibold text-gray-500 text-xs tracking-wider uppercase whitespace-nowrap" data-id="element-2737" data-mp="19501yak277p">
                  1M Trend
                </th>
                <th className="px-6 py-4 font-semibold text-gray-500 text-xs tracking-wider uppercase whitespace-nowrap" data-id="element-2738" data-mp="a91ted7w3u93">
                  DBT
                </th>
                <th className="px-6 py-4 font-semibold text-gray-500 text-xs tracking-wider uppercase whitespace-nowrap" data-id="element-2739" data-mp="ow3c8jrn6szt">
                  Balance / Limit
                </th>
                <th className="px-6 py-4 font-semibold text-gray-500 text-xs tracking-wider uppercase whitespace-nowrap" data-id="element-2740" data-mp="xxb9cyif9nsb">
                  Assigned To
                </th>
                <th className="px-6 py-4 font-semibold text-gray-500 text-xs tracking-wider uppercase whitespace-nowrap" data-id="element-2741" data-mp="2zpfxs00pq2ya">
                  Group
                </th>
                <th className="px-6 py-4 font-semibold text-gray-500 text-xs tracking-wider uppercase text-right whitespace-nowrap" data-id="element-2742" data-mp="2vqqlkareurg8">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100" data-id="element-2743" data-mp="2buw1wxey5odh">
              {filteredData.map(row => {
              const utilPct = typeof row.limit === 'number' && row.limit > 0 ? Math.round(row.balance / row.limit * 100) : 0;
              const isDunderMifflin = row.id === '2';
              const isWonka = row.id === '1';
              const isInitech = row.id === '5';
              const isClickable = isDunderMifflin || isWonka || isInitech;
              return <tr key={row.id} onClick={isClickable ? () => onCompanyClick(isWonka ? 'wonka' : isInitech ? 'initech' : 'dunder') : undefined} className={`group transition-all ${isClickable ? 'cursor-pointer hover:bg-indigo-100' : 'hover:bg-indigo-50/30'}`} data-id="element-2744" data-mp="1xvo1k171sa7p">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2745" data-mp="dckjftsu4yyf">
                      <span className="font-bold text-gray-900" data-id="element-2746" data-mp="dc132hiu2imi">
                        {row.company}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2747" data-mp="3m91h3cbgp8j">
                      <div className={`inline-flex items-center justify-center w-10 h-10 rounded-full border-2 font-bold ${getScoreColor(row.score)}`} data-id="element-2748" data-mp="3utbum0bpt5le">
                        {row.score}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2749" data-mp="rasaxm73r827">
                      <div className={`flex items-center gap-1 font-semibold ${getTrendColor(row.trend)}`} data-id="element-2750" data-mp="yp1i381y9h20">
                        {row.trend < 0 ? <ArrowDownRight className="w-4 h-4" data-id="element-2751" data-mp="18l4hw79bx3ls" /> : <ArrowUpRight className="w-4 h-4" data-id="element-2752" data-mp="1dls8wsxk5kg0" />}
                        {Math.abs(row.trend)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2753" data-mp="1mkxbqbg5jzxz">
                      <span className="text-gray-900 font-medium" data-id="element-2754" data-mp="1rcf78jzep8bk">
                        {row.dbt}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2755" data-mp="1waywoud7l28o">
                      <div className="w-48" data-id="element-2756" data-mp="21b4qag31gyte">
                        {row.limit === 'Pending Decision' ? <span className="text-yellow-600 text-sm font-semibold" data-id="element-2757" data-mp="25bevhqj0bl77">
                            Pending Decision
                          </span> : row.limit > 0 ? <>
                            <div className="flex justify-between text-xs mb-1.5" data-id="element-2758" data-mp="2ml9z92fd2jmr">
                              <span className="font-semibold text-gray-900" data-id="element-2759" data-mp="30ksyf5q72kmx">
                                {formatCurrency(row.balance)}
                              </span>
                              <span className="text-gray-500 font-medium" data-id="element-2760" data-mp="27vvp3s3yny6b">
                                {utilPct}%
                              </span>
                            </div>
                            <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden mb-1" data-id="element-2761" data-mp="2hmyperp2g94j">
                              <div className="h-full bg-indigo-500 rounded-full" style={{
                          width: `${utilPct}%`
                        }} data-id="element-2762" data-mp="2cvgp85uvackm" />
                            </div>
                            <div className="text-right text-[10px] text-gray-400 font-medium" data-id="element-2763" data-mp="3vtda4kvp8ls8">
                              {formatCurrency(row.limit)} limit
                            </div>
                          </> : <span className="text-gray-500 text-sm" data-id="element-2764" data-mp="3mrx40pbx35tt">
                            No credit extended
                          </span>}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2765" data-mp="nf4ra4izb9d9">
                      <span className="text-gray-600" data-id="element-2766" data-mp="ncig7uwvqbqa">{row.assignedTo}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2767" data-mp="9h6db08kleya">
                      <div className="w-8 h-8 rounded-full bg-slate-800 text-white flex items-center justify-center text-xs font-bold" data-id="element-2768" data-mp="eclsbvibdgwa">
                        {row.group}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2769" data-mp="2z22o1ywax15c">
                      <div className="flex justify-center" data-id="element-2770" data-mp="39euo4dajgqt3">
                        <motion.button whileHover={{
                      scale: 1.05
                    }} whileTap={{
                      scale: 0.95
                    }} className={`px-6 py-2 rounded-lg text-white text-sm font-semibold transition-colors ${isClickable ? 'bg-indigo-600 hover:bg-indigo-700 cursor-pointer' : 'bg-indigo-400 cursor-not-allowed'}`} data-id="element-2771" data-mp="3ecco3e6kvvoy">
                          View
                        </motion.button>
                      </div>
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}